import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Select } from '../components/Select';
import { FileUpload } from '../components/FileUpload';
import { UserIcon } from '../components/Icons';

export const WriterOnboardingPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Profile basics
    displayName: '',
    bio: '',
    profilePhoto: null as File | null,
    // Step 2: Writing details
    primaryGenre: '',
    experienceLevel: '',
    // Step 3: Preferences
    emailNotifications: true,
    weeklyDigest: false,
    writingGoals: '',
  });

  const genres = [
    { value: 'literary-fiction', label: 'Literary Fiction' },
    { value: 'mystery', label: 'Mystery' },
    { value: 'thriller', label: 'Thriller' },
    { value: 'romance', label: 'Romance' },
    { value: 'fantasy', label: 'Fantasy' },
    { value: 'science-fiction', label: 'Science Fiction' },
    { value: 'historical-fiction', label: 'Historical Fiction' },
    { value: 'horror', label: 'Horror' },
    { value: 'young-adult', label: 'Young Adult' },
    { value: 'memoir', label: 'Memoir' },
    { value: 'other', label: 'Other' },
  ];

  const experienceLevels = [
    { value: 'first-manuscript', label: 'This is my first manuscript' },
    { value: 'some-experience', label: 'I\'ve written a few manuscripts' },
    { value: 'experienced', label: 'I\'m an experienced writer' },
    { value: 'published', label: 'I\'m published' },
  ];

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    console.log('Onboarding completed:', formData);
    window.location.href = '/writer/dashboard';
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header showAuth={false} />

      <main style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Progress Indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '40px',
        }}>
          {[1, 2, 3].map((num) => (
            <React.Fragment key={num}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: step >= num ? theme.colors.primary : theme.colors.border,
                color: step >= num ? theme.colors.white : theme.colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '500',
                fontSize: '14px',
                fontFamily: theme.fonts.monospace,
              }}>
                {num}
              </div>
              {num < 3 && (
                <div style={{
                  width: '60px',
                  height: '2px',
                  backgroundColor: step > num ? theme.colors.primary : theme.colors.border,
                }}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Title */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
        }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            {step === 1 && 'Welcome to Manuscript Match'}
            {step === 2 && 'Tell Us About Your Writing'}
            {step === 3 && 'Set Your Preferences'}
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            {step === 1 && 'Let\'s start with your basic profile'}
            {step === 2 && 'Help us understand your writing journey'}
            {step === 3 && 'Customize your experience'}
          </p>
        </div>

        {/* Form Card */}
        <div style={{
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.xlarge,
          padding: '40px',
          border: `1px solid ${theme.colors.border}`,
          marginBottom: '24px',
        }}>
          {/* Step 1: Profile Basics */}
          {step === 1 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}>
              <FileUpload
                label="Profile Photo"
                onChange={(file) => setFormData({ ...formData, profilePhoto: file })}
                helperText="Optional - You can add this later"
              />

              <Input
                label="Display Name"
                type="text"
                placeholder="How should we address you?"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                icon={<UserIcon />}
              />

              <Textarea
                label="Bio"
                placeholder="Tell us a bit about yourself and your writing..."
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                maxLength={500}
                rows={5}
                helperText="Optional - Share what inspires your writing"
              />
            </div>
          )}

          {/* Step 2: Writing Details */}
          {step === 2 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}>
              <Select
                label="Primary Genre"
                value={formData.primaryGenre}
                onChange={(e) => setFormData({ ...formData, primaryGenre: e.target.value })}
                options={genres}
                placeholder="Select your main genre"
                helperText="You can add more genres later"
              />

              <Select
                label="Experience Level"
                value={formData.experienceLevel}
                onChange={(e) => setFormData({ ...formData, experienceLevel: e.target.value })}
                options={experienceLevels}
                placeholder="Select your experience level"
                helperText="This helps us match you with appropriate readers"
              />

              <div style={{
                backgroundColor: theme.colors.backgroundAlt,
                borderRadius: theme.borderRadius.large,
                padding: '20px',
                fontSize: '15px',
                lineHeight: '1.6',
                color: theme.colors.textSecondary,
              }}>
                <strong style={{ color: theme.colors.textPrimary }}>Tip:</strong> Beta readers
                appreciate knowing your experience level. It helps them provide feedback
                that's right for where you are in your writing journey.
              </div>
            </div>
          )}

          {/* Step 3: Preferences */}
          {step === 3 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}>
              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                }}>
                  Notification Preferences
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: theme.borderRadius.medium,
                    backgroundColor: theme.colors.backgroundAlt,
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.emailNotifications}
                      onChange={(e) => setFormData({ ...formData, emailNotifications: e.target.checked })}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                      }}
                    />
                    <div>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                      }}>
                        Email notifications
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: theme.colors.textSecondary,
                      }}>
                        Get notified about beta reader applications and feedback
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '12px',
                    borderRadius: theme.borderRadius.medium,
                    backgroundColor: theme.colors.backgroundAlt,
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.weeklyDigest}
                      onChange={(e) => setFormData({ ...formData, weeklyDigest: e.target.checked })}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                      }}
                    />
                    <div>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                      }}>
                        Weekly digest
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: theme.colors.textSecondary,
                      }}>
                        Receive a weekly summary of your activity
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <Textarea
                label="Writing Goals"
                placeholder="What are you hoping to achieve with your manuscript? (Optional)"
                value={formData.writingGoals}
                onChange={(e) => setFormData({ ...formData, writingGoals: e.target.value })}
                maxLength={300}
                rows={4}
                helperText="Share your goals to help us support your journey"
              />
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
        }}>
          <div>
            {step > 1 && (
              <Button variant="secondary" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            )}
          </div>
          <div style={{
            display: 'flex',
            gap: '12px',
          }}>
            <Button variant="ghost" onClick={handleSkip}>
              Skip
            </Button>
            <Button onClick={handleNext}>
              {step === 3 ? 'Complete' : 'Continue'}
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
